import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users, Shield, Zap, Building2, BarChart3, UserCog, Play, Eye,
  EyeOff, Loader2, Search, ToggleLeft, ToggleRight, Crown, AlertTriangle,
  CheckCircle2, XCircle, LogOut, Activity, Clock, BookOpen,
  GraduationCap, ClipboardCheck, Megaphone, Plus, Trash2, Settings2,
  TrendingUp, RefreshCw, Ban, ChevronRight
} from "lucide-react";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Progress } from "../../components/ui/progress";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { cn } from "../../lib/utils";

const TABS = [
  { id: "usage", label: "Usage Dashboard", icon: BarChart3 },
  { id: "impersonation", label: "Impersonation", icon: UserCog },
  { id: "sessions", label: "Active Sessions", icon: Activity },
  { id: "features", label: "Feature Flags", icon: Zap },
  { id: "licensing", label: "Licensing", icon: Crown },
  { id: "announcements", label: "Platform Alerts", icon: Megaphone },
];

const TIER_COLORS = {
  free: "bg-gray-50 text-gray-700 border-gray-200",
  starter: "bg-blue-50 text-blue-700 border-blue-200",
  professional: "bg-violet-50 text-violet-700 border-violet-200",
  enterprise: "bg-amber-50 text-amber-700 border-amber-200",
};

const AdminOpsPage = () => {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("usage");
  const [loading, setLoading] = useState(true);

  // Usage
  const [usage, setUsage] = useState(null);

  // Impersonation
  const [allUsers, setAllUsers] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [impersonating, setImpersonating] = useState(false);

  // Sessions
  const [sessions, setSessions] = useState([]);

  // Features
  const [featureData, setFeatureData] = useState(null);
  const [selectedTenant, setSelectedTenant] = useState("");
  const [tenantFlags, setTenantFlags] = useState(null);
  const [flagLoading, setFlagLoading] = useState("");

  // Licensing
  const [licenses, setLicenses] = useState([]);
  const [tiers, setTiers] = useState({});

  // Announcements
  const [announcements, setAnnouncements] = useState([]);
  const [showAnnouncementForm, setShowAnnouncementForm] = useState(false);
  const [announcementForm, setAnnouncementForm] = useState({ title: "", message: "", type: "info", show_banner: false });

  // Tenants
  const [tenants, setTenants] = useState([]);

  const fetchUsage = async () => {
    try { const r = await api.get("/admin/usage-dashboard"); setUsage(r.data); } catch {}
    finally { setLoading(false); }
  };

  const fetchUsers = async () => {
    try { const r = await api.get("/users"); setAllUsers(r.data || []); } catch {}
  };

  const fetchSessions = async () => {
    try { const r = await api.get("/admin/active-sessions"); setSessions(r.data || []); } catch {}
  };

  const fetchFeatures = async () => {
    try { const r = await api.get("/admin/feature-flags"); setFeatureData(r.data); } catch {}
  };

  const fetchTenantFlags = async (tid) => {
    try { const r = await api.get(`/admin/feature-flags/${tid}`); setTenantFlags(r.data); } catch {}
  };

  const fetchLicenses = async () => {
    try {
      const [lic, t] = await Promise.all([api.get("/admin/tenant-licenses"), api.get("/admin/subscription-tiers")]);
      setLicenses(lic.data || []);
      setTiers(t.data || {});
    } catch {}
  };

  const fetchAnnouncements = async () => {
    try { const r = await api.get("/admin/platform-announcements"); setAnnouncements(r.data || []); } catch {}
  };

  const fetchTenants = async () => {
    try { const r = await api.get("/tenants"); setTenants(r.data || []); } catch {}
  };

  useEffect(() => { fetchUsage(); fetchTenants(); }, []);
  useEffect(() => {
    if (activeTab === "impersonation") fetchUsers();
    if (activeTab === "sessions") fetchSessions();
    if (activeTab === "features") fetchFeatures();
    if (activeTab === "licensing") fetchLicenses();
    if (activeTab === "announcements") fetchAnnouncements();
  }, [activeTab]);

  useEffect(() => {
    if (selectedTenant) fetchTenantFlags(selectedTenant);
    else setTenantFlags(null);
  }, [selectedTenant]);

  const handleImpersonate = async (targetUser) => {
    if (!window.confirm(`Impersonate ${targetUser.full_name || targetUser.email}?\nYou will be logged in as this user.`)) return;
    setImpersonating(true);
    try {
      const r = await api.post(`/admin/impersonate/${targetUser.id}`);
      // Store original admin info
      localStorage.setItem("odesie_impersonation", JSON.stringify({
        original_admin_id: user.id,
        original_token: localStorage.getItem("odesie_token") || localStorage.getItem("token"),
        target_name: targetUser.full_name,
      }));
      // Login as target user
      localStorage.setItem("odesie_token", r.data.access_token);
      localStorage.setItem("token", r.data.access_token);
      toast.success(`Now impersonating ${targetUser.full_name || targetUser.email}`);
      window.location.reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Impersonation failed"); }
    finally { setImpersonating(false); }
  };

  const handleForceLogout = async (userId, name) => {
    if (!window.confirm(`Force logout ${name}?`)) return;
    try { await api.post(`/admin/force-logout/${userId}`); toast.success(`Force logout: ${name}`); } catch { toast.error("Failed"); }
  };

  const handleSuspend = async (userId, name) => {
    if (!window.confirm(`Toggle suspend for ${name}?`)) return;
    try {
      const r = await api.post(`/admin/suspend-user/${userId}`);
      toast.success(r.data.message);
      fetchSessions();
      fetchUsers();
    } catch { toast.error("Failed"); }
  };

  const handleToggleFlag = async (feature, enabled) => {
    if (!selectedTenant) return;
    setFlagLoading(feature);
    try {
      await api.put(`/admin/feature-flags/${selectedTenant}/${feature}`, { enabled });
      toast.success(`${feature} ${enabled ? "enabled" : "disabled"}`);
      fetchTenantFlags(selectedTenant);
    } catch { toast.error("Failed"); }
    finally { setFlagLoading(""); }
  };

  const handleUpdateLicense = async (tenantId, tier) => {
    try {
      await api.put(`/admin/tenant-licenses/${tenantId}`, { tier });
      toast.success("License updated");
      fetchLicenses();
    } catch { toast.error("Failed"); }
  };

  const handleCreateAnnouncement = async () => {
    if (!announcementForm.title) { toast.error("Title required"); return; }
    try {
      await api.post("/admin/platform-announcements", announcementForm);
      toast.success("Announcement created");
      setShowAnnouncementForm(false);
      setAnnouncementForm({ title: "", message: "", type: "info", show_banner: false });
      fetchAnnouncements();
    } catch { toast.error("Failed"); }
  };

  const handleDeleteAnnouncement = async (id) => {
    try { await api.delete(`/admin/platform-announcements/${id}`); toast.success("Deleted"); fetchAnnouncements(); }
    catch { toast.error("Failed"); }
  };

  const filteredUsers = allUsers.filter(u =>
    u.role !== "super_admin" && (!userSearch ||
    u.full_name?.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.email?.toLowerCase().includes(userSearch.toLowerCase()))
  );

  const ALERT_COLORS = { info: "bg-blue-50 text-blue-700 border-blue-200", warning: "bg-amber-50 text-amber-700 border-amber-200", critical: "bg-red-50 text-red-700 border-red-200", maintenance: "bg-violet-50 text-violet-700 border-violet-200" };

  return (
    <AdminLayout title="Platform Operations">
      <div className="space-y-5 max-w-7xl" data-testid="admin-ops-page">
        <div>
          <h1 className="text-xl font-bold" data-testid="ops-title">Platform Operations Center</h1>
          <p className="text-sm text-muted-foreground">Manage tenants, impersonate users, control features, and monitor platform health</p>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 border-b overflow-x-auto">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={cn("px-3 py-2 text-xs font-medium border-b-2 -mb-px whitespace-nowrap transition-colors flex items-center gap-1.5", activeTab === t.id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground")}
              data-testid={`ops-tab-${t.id}`}>
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>

        {/* ═══ USAGE DASHBOARD ═══ */}
        {activeTab === "usage" && (
          <div className="space-y-5">
            {loading ? <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div> : usage && (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
                  {[
                    { l: "Total Users", v: usage.overview.total_users, icon: Users, c: "text-blue-600 bg-blue-50" },
                    { l: "Active Users", v: usage.overview.active_users, icon: Activity, c: "text-green-600 bg-green-50" },
                    { l: "Tenants", v: usage.overview.total_tenants, icon: Building2, c: "text-violet-600 bg-violet-50" },
                    { l: "Courses", v: `${usage.overview.published_courses}/${usage.overview.total_courses}`, icon: BookOpen, c: "text-amber-600 bg-amber-50" },
                    { l: "Enrollments", v: usage.overview.total_enrollments, icon: GraduationCap, c: "text-primary bg-primary/10" },
                    { l: "Completion", v: `${usage.overview.completion_rate}%`, icon: CheckCircle2, c: "text-green-600 bg-green-50" },
                  ].map((s, i) => (
                    <Card key={i} className="border shadow-none"><CardContent className="p-3 flex items-center gap-2.5">
                      <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center shrink-0", s.c)}><s.icon className="w-4 h-4" /></div>
                      <div><p className="text-lg font-bold leading-none">{s.v}</p><p className="text-[10px] text-muted-foreground">{s.l}</p></div>
                    </CardContent></Card>
                  ))}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  <Card className="border shadow-none">
                    <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Users by Tenant</CardTitle></CardHeader>
                    <CardContent className="px-5 pb-5 space-y-2">
                      {usage.users_by_tenant?.map((t, i) => (
                        <div key={i} className="flex items-center justify-between p-2.5 rounded-lg border">
                          <span className="text-xs font-medium truncate">{t.tenant_name || "Platform"}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-bold">{t.count}</span>
                            <div className="w-16 h-1.5 rounded-full bg-muted overflow-hidden">
                              <div className="h-full rounded-full bg-primary" style={{ width: `${Math.min((t.count / Math.max(usage.overview.total_users, 1)) * 100, 100)}%` }} />
                            </div>
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>

                  <Card className="border shadow-none">
                    <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Enrollments by Tenant</CardTitle></CardHeader>
                    <CardContent className="px-5 pb-5 space-y-2">
                      {usage.enrollments_by_tenant?.map((t, i) => (
                        <div key={i} className="flex items-center justify-between p-2.5 rounded-lg border">
                          <span className="text-xs font-medium truncate">{t.tenant_name || "Platform"}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-bold">{t.count}</span>
                            <div className="w-16 h-1.5 rounded-full bg-muted overflow-hidden">
                              <div className="h-full rounded-full bg-green-500" style={{ width: `${Math.min((t.count / Math.max(usage.overview.total_enrollments, 1)) * 100, 100)}%` }} />
                            </div>
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { l: "ILT Sessions", v: usage.overview.total_sessions, icon: Play },
                    { l: "Assessments", v: usage.overview.total_assessments, icon: ClipboardCheck },
                    { l: "Compliance Templates", v: usage.overview.total_compliance, icon: Shield },
                    { l: "Master Templates", v: usage.overview.total_templates, icon: Zap },
                  ].map((s, i) => (
                    <Card key={i} className="border shadow-none"><CardContent className="p-3 flex items-center gap-2.5">
                      <s.icon className="w-4 h-4 text-muted-foreground" /><div><p className="text-lg font-bold leading-none">{s.v}</p><p className="text-[10px] text-muted-foreground">{s.l}</p></div>
                    </CardContent></Card>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* ═══ IMPERSONATION ═══ */}
        {activeTab === "impersonation" && (
          <div className="space-y-4">
            <Card className="border shadow-none border-amber-200 bg-amber-50/30">
              <CardContent className="p-4 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-amber-800">Impersonation Mode</p>
                  <p className="text-xs text-amber-700 mt-0.5">When you impersonate a user, you will see the platform exactly as they do. All actions are logged in the audit trail. Use this for debugging and support.</p>
                </div>
              </CardContent>
            </Card>

            <div className="relative max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search users by name or email..." value={userSearch} onChange={e => setUserSearch(e.target.value)} className="pl-10 h-9 text-sm" data-testid="impersonate-search" />
            </div>

            <Card className="border shadow-none overflow-hidden" data-testid="impersonation-table">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr className="border-b bg-muted/30">
                    <th className="p-3 text-left text-[10px] font-semibold uppercase text-muted-foreground">User</th>
                    <th className="p-3 text-center text-[10px] font-semibold uppercase text-muted-foreground">Role</th>
                    <th className="p-3 text-center text-[10px] font-semibold uppercase text-muted-foreground">Tenant</th>
                    <th className="p-3 text-center text-[10px] font-semibold uppercase text-muted-foreground">Status</th>
                    <th className="p-3 text-right text-[10px] font-semibold uppercase text-muted-foreground">Actions</th>
                  </tr></thead>
                  <tbody>
                    {filteredUsers.length === 0 ? (
                      <tr><td colSpan={5} className="p-8 text-center text-sm text-muted-foreground">No users found</td></tr>
                    ) : filteredUsers.slice(0, 30).map(u => (
                      <tr key={u.id} className="border-b hover:bg-muted/20">
                        <td className="p-3"><div><p className="text-sm font-medium">{u.full_name}</p><p className="text-[10px] text-muted-foreground">{u.email}</p></div></td>
                        <td className="p-3 text-center"><Badge variant="outline" className="text-[9px] capitalize">{u.role?.replace("_", " ")}</Badge></td>
                        <td className="p-3 text-center"><span className="text-xs text-muted-foreground">{u.tenant_id || "-"}</span></td>
                        <td className="p-3 text-center">
                          <Badge className={cn("text-[9px] px-1.5 py-0 border", u.status === "suspended" ? "bg-red-50 text-red-600 border-red-200" : "bg-green-50 text-green-700 border-green-200")}>
                            {u.status || "active"}
                          </Badge>
                        </td>
                        <td className="p-3 text-right">
                          <div className="flex items-center gap-1 justify-end">
                            <Button size="sm" variant="outline" className="h-7 text-[10px] gap-1" onClick={() => handleImpersonate(u)} disabled={impersonating || u.status === "suspended"} data-testid={`impersonate-${u.id}`}>
                              <Eye className="w-3 h-3" /> Impersonate
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-muted-foreground hover:text-red-500" onClick={() => handleSuspend(u.id, u.full_name)} title={u.status === "suspended" ? "Reactivate" : "Suspend"}>
                              <Ban className="w-3 h-3" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        )}

        {/* ═══ ACTIVE SESSIONS ═══ */}
        {activeTab === "sessions" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">Recently active users across all tenants</p>
              <Button variant="outline" size="sm" className="text-xs h-7 gap-1" onClick={fetchSessions}><RefreshCw className="w-3 h-3" /> Refresh</Button>
            </div>
            <Card className="border shadow-none overflow-hidden" data-testid="sessions-table">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr className="border-b bg-muted/30">
                    <th className="p-3 text-left text-[10px] font-semibold uppercase text-muted-foreground">User</th>
                    <th className="p-3 text-center text-[10px] font-semibold uppercase text-muted-foreground">Role</th>
                    <th className="p-3 text-center text-[10px] font-semibold uppercase text-muted-foreground">Tenant</th>
                    <th className="p-3 text-center text-[10px] font-semibold uppercase text-muted-foreground">Last Login</th>
                    <th className="p-3 text-right text-[10px] font-semibold uppercase text-muted-foreground">Actions</th>
                  </tr></thead>
                  <tbody>
                    {sessions.length === 0 ? (
                      <tr><td colSpan={5} className="p-8 text-center text-sm text-muted-foreground">No active sessions</td></tr>
                    ) : sessions.map(s => (
                      <tr key={s.user_id} className="border-b hover:bg-muted/20">
                        <td className="p-3"><div><p className="text-sm font-medium">{s.full_name}</p><p className="text-[10px] text-muted-foreground">{s.email}</p></div></td>
                        <td className="p-3 text-center"><Badge variant="outline" className="text-[9px] capitalize">{s.role?.replace("_", " ")}</Badge></td>
                        <td className="p-3 text-center"><span className="text-xs text-muted-foreground">{s.tenant_id || "-"}</span></td>
                        <td className="p-3 text-center"><span className="text-xs text-muted-foreground">{s.last_login ? new Date(s.last_login).toLocaleString() : "-"}</span></td>
                        <td className="p-3 text-right">
                          <Button size="sm" variant="ghost" className="h-7 text-[10px] gap-1 text-red-600 hover:bg-red-50" onClick={() => handleForceLogout(s.user_id, s.full_name)}>
                            <LogOut className="w-3 h-3" /> Force Logout
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        )}

        {/* ═══ FEATURE FLAGS ═══ */}
        {activeTab === "features" && (
          <div className="space-y-5">
            <div className="flex items-center gap-3">
              <Label className="text-xs shrink-0">Select Tenant:</Label>
              <Select value={selectedTenant} onValueChange={setSelectedTenant}>
                <SelectTrigger className="h-8 text-xs w-64" data-testid="feature-tenant-select"><SelectValue placeholder="Choose a tenant..." /></SelectTrigger>
                <SelectContent>
                  {tenants.map(t => <SelectItem key={t.id} value={t.id}>{t.name || t.company_name || t.id}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {!selectedTenant ? (
              <Card className="border shadow-none"><CardContent className="py-12 text-center">
                <Zap className="w-10 h-10 mx-auto text-muted-foreground/20 mb-3" />
                <p className="text-sm text-muted-foreground">Select a tenant to manage their feature flags</p>
              </CardContent></Card>
            ) : tenantFlags ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="feature-flags-grid">
                {Object.entries(tenantFlags.flags || {}).map(([key, flag]) => (
                  <Card key={key} className={cn("border shadow-none transition-colors", flag.enabled ? "border-green-200 bg-green-50/30" : "")}>
                    <CardContent className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3 min-w-0">
                        <Zap className={cn("w-5 h-5 shrink-0", flag.enabled ? "text-green-600" : "text-muted-foreground/30")} />
                        <div className="min-w-0">
                          <p className="text-sm font-medium">{flag.label}</p>
                          <p className="text-[10px] text-muted-foreground">{flag.description}</p>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" className={cn("h-8 w-14 p-0", flag.enabled ? "text-green-600" : "text-muted-foreground")}
                        onClick={() => handleToggleFlag(key, !flag.enabled)} disabled={flagLoading === key}
                        data-testid={`toggle-${key}`}>
                        {flagLoading === key ? <Loader2 className="w-4 h-4 animate-spin" /> : flag.enabled ? <ToggleRight className="w-7 h-7" /> : <ToggleLeft className="w-7 h-7" />}
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>}
          </div>
        )}

        {/* ═══ LICENSING ═══ */}
        {activeTab === "licensing" && (
          <div className="space-y-5">
            {/* Tier Overview */}
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              {Object.entries(tiers).map(([key, tier]) => (
                <Card key={key} className="border shadow-none">
                  <CardContent className="p-4">
                    <Badge className={cn("text-[10px] px-2 py-0.5 border mb-2", TIER_COLORS[key])}>{tier.label}</Badge>
                    <div className="space-y-1 text-xs">
                      <p>Users: <span className="font-bold">{tier.max_users === -1 ? "Unlimited" : tier.max_users}</span></p>
                      <p>Courses: <span className="font-bold">{tier.max_courses === -1 ? "Unlimited" : tier.max_courses}</span></p>
                      <p className="text-[10px] text-muted-foreground">{tier.features?.length} features</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card className="border shadow-none overflow-hidden" data-testid="licensing-table">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr className="border-b bg-muted/30">
                    <th className="p-3 text-left text-[10px] font-semibold uppercase text-muted-foreground">Tenant</th>
                    <th className="p-3 text-center text-[10px] font-semibold uppercase text-muted-foreground">Tier</th>
                    <th className="p-3 text-center text-[10px] font-semibold uppercase text-muted-foreground">Users</th>
                    <th className="p-3 text-center text-[10px] font-semibold uppercase text-muted-foreground">Courses</th>
                    <th className="p-3 text-center text-[10px] font-semibold uppercase text-muted-foreground">Status</th>
                    <th className="p-3 text-right text-[10px] font-semibold uppercase text-muted-foreground">Change Tier</th>
                  </tr></thead>
                  <tbody>
                    {licenses.map(l => (
                      <tr key={l.tenant_id} className="border-b hover:bg-muted/20">
                        <td className="p-3"><p className="text-sm font-medium">{l.tenant_name || l.tenant_id}</p></td>
                        <td className="p-3 text-center"><Badge className={cn("text-[9px] px-1.5 py-0 border", TIER_COLORS[l.tier])}>{l.tier_label}</Badge></td>
                        <td className="p-3 text-center">
                          <span className="text-sm font-medium">{l.current_users}</span>
                          <span className="text-[10px] text-muted-foreground">/{l.max_users === -1 ? "∞" : l.max_users}</span>
                        </td>
                        <td className="p-3 text-center">
                          <span className="text-sm font-medium">{l.current_courses}</span>
                          <span className="text-[10px] text-muted-foreground">/{l.max_courses === -1 ? "∞" : l.max_courses}</span>
                        </td>
                        <td className="p-3 text-center"><Badge className="text-[9px] bg-green-50 text-green-700 border-green-200">{l.status}</Badge></td>
                        <td className="p-3 text-right">
                          <Select value={l.tier} onValueChange={v => handleUpdateLicense(l.tenant_id, v)}>
                            <SelectTrigger className="h-7 text-[10px] w-28"><SelectValue /></SelectTrigger>
                            <SelectContent>{Object.entries(tiers).map(([k, t]) => <SelectItem key={k} value={k} className="text-xs">{t.label}</SelectItem>)}</SelectContent>
                          </Select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        )}

        {/* ═══ PLATFORM ANNOUNCEMENTS ═══ */}
        {activeTab === "announcements" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">System-wide announcements visible to all users</p>
              <Button size="sm" className="text-xs h-7 gap-1" onClick={() => setShowAnnouncementForm(true)} data-testid="create-announcement-btn">
                <Plus className="w-3 h-3" /> New Announcement
              </Button>
            </div>

            {announcements.length === 0 ? (
              <Card className="border shadow-none"><CardContent className="py-12 text-center">
                <Megaphone className="w-10 h-10 mx-auto text-muted-foreground/20 mb-3" />
                <p className="text-sm text-muted-foreground">No platform announcements</p>
              </CardContent></Card>
            ) : (
              <div className="space-y-3" data-testid="announcements-list">
                {announcements.map(a => (
                  <Card key={a.id} className={cn("border shadow-none", ALERT_COLORS[a.type])}>
                    <CardContent className="p-4 flex items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline" className="text-[9px] capitalize">{a.type}</Badge>
                          {a.show_banner && <Badge className="text-[9px] bg-primary/10 text-primary border-primary/20">Banner</Badge>}
                          {!a.is_active && <Badge className="text-[9px] bg-muted text-muted-foreground">Inactive</Badge>}
                        </div>
                        <p className="text-sm font-semibold">{a.title}</p>
                        {a.message && <p className="text-xs mt-1 opacity-80">{a.message}</p>}
                        <p className="text-[10px] opacity-60 mt-2">By {a.created_by_name} &middot; {new Date(a.created_at).toLocaleString()}</p>
                      </div>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 shrink-0" onClick={() => handleDeleteAnnouncement(a.id)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            <Dialog open={showAnnouncementForm} onOpenChange={setShowAnnouncementForm}>
              <DialogContent className="max-w-md">
                <DialogHeader><DialogTitle className="text-base">New Platform Announcement</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label className="text-xs">Title</Label><Input value={announcementForm.title} onChange={e => setAnnouncementForm({...announcementForm, title: e.target.value})} className="h-8 text-sm" /></div>
                  <div><Label className="text-xs">Message</Label><Textarea value={announcementForm.message} onChange={e => setAnnouncementForm({...announcementForm, message: e.target.value})} rows={3} className="text-sm" /></div>
                  <div><Label className="text-xs">Type</Label>
                    <Select value={announcementForm.type} onValueChange={v => setAnnouncementForm({...announcementForm, type: v})}>
                      <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="info">Info</SelectItem><SelectItem value="warning">Warning</SelectItem><SelectItem value="critical">Critical</SelectItem><SelectItem value="maintenance">Maintenance</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={announcementForm.show_banner} onChange={e => setAnnouncementForm({...announcementForm, show_banner: e.target.checked})} className="rounded" /> Show as banner at top of all pages</label>
                </div>
                <DialogFooter>
                  <Button variant="outline" size="sm" className="text-xs" onClick={() => setShowAnnouncementForm(false)}>Cancel</Button>
                  <Button size="sm" className="text-xs gap-1" onClick={handleCreateAnnouncement}><CheckCircle2 className="w-3 h-3" /> Create</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminOpsPage;
